import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from 'react-bootstrap/Button';

import { updateProductOrder } from 'actions/ShoppingCart';
import {
  getCustomPromptsForProduct,
  getSerialNumberTitle,
  getTextAroundSerialNumber,
} from 'reducers/configs/settings';
import CustomPromptsForm from 'containers/Forms/CustomPrompts/CustomPromptsForm';
import useCustomPromptsFormValidation from 'containers/Forms/CustomPrompts/useCustomPromptsFormValidation';
import { Product } from 'types/Product';
import { Order } from 'types/ShoppingCart';
import {
  getCustomPromptValuesAndSeparatorUsedFromItem,
  getEmbeddedProductWithCustomPromptInformation,
} from 'utils/customPrompts';

const SerialNumberEditForm = ({
  product,
  order,
}: {
  product: Product;
  order: Order;
}) => {
  const { t } = useTranslation(['shoppingCart', 'common']);

  const dispatch = useDispatch();

  const textAroundSerialNumber = useSelector(getTextAroundSerialNumber);

  const customPrompts = useSelector(
    getCustomPromptsForProduct(order?.productID),
  ) as string[];

  const {
    invalidPrompts,
    validate,
    getErrorText,
  } = useCustomPromptsFormValidation();

  const customPromptsMap = useMemo(() => {
    const { customPromptDetails, separator } =
      getCustomPromptValuesAndSeparatorUsedFromItem({
        item: order as any,
        separator: textAroundSerialNumber,
        customPrompts,
      }) ?? {};
    // Check for string specifically since separator may be ''
    if (customPromptDetails && typeof separator === 'string') {
      return {
        prompts: customPromptDetails,
        separator,
      };
    }
    const [firstSymbol = '', secondSymbol = ''] = textAroundSerialNumber.split(
      ',',
    );
    const prompts = String(order.name).replace(product.name, '');
    if (!prompts) {
      return {
        prompts: {},
        separator: textAroundSerialNumber,
      };
    }
    const promptDict = prompts
      .trim()
      .replace(
        firstSymbol && secondSymbol
          ? new RegExp(`[${firstSymbol}${secondSymbol}]`, 'g')
          : /[()]/g,
        '',
      )
      .split(', ')
      .slice(product.hasSerialNumbers ? 1 : 0)
      .reduce((all, prev) => {
        const [name, value] = prev.split(': ');
        return { ...all, [name]: value };
      }, {});
    return {
      prompts: promptDict,
      separator: textAroundSerialNumber,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.name]);

  const serialNumberTitle = useSelector(getSerialNumberTitle);

  const [serialNumberValue, setSerialNumberValue] = useState(
    Object.fromEntries([[serialNumberTitle, order.serialNumber]]),
  );
  const [
    customPromptsAndSeparatorValue,
    setCustomPromptsAndSeparatorValue,
  ] = useState(customPromptsMap);

  const handleSave = () => {
    const serialNumberPart = product.hasSerialNumbers
      ? `${serialNumberTitle}: ${serialNumberValue[serialNumberTitle]}`
      : '';

    const existingSerialNumber = order.serialNumber
      ? `${serialNumberTitle}: ${order.serialNumber}`
      : undefined;

    const newOrder = getEmbeddedProductWithCustomPromptInformation({
      product: order,
      newValues: customPromptsAndSeparatorValue.prompts,
      separator: customPromptsAndSeparatorValue.separator,
      existingSerialNumber,
      serialNumber: serialNumberPart,
    });
    dispatch(updateProductOrder(newOrder));
  };

  return (
    <div className="product-serial-number">
      <div className="serial-number-header">
        <h5>{t('inputs.serialNumber')}</h5>
        <Button onClick={handleSave} disabled={!!invalidPrompts.length}>
          {t('save_changes', { ns: 'common' })}
        </Button>
      </div>
      {!!product.hasSerialNumbers && (
        <CustomPromptsForm
          prompts={[serialNumberTitle]}
          value={serialNumberValue}
          onChange={(sn, v) => setSerialNumberValue({ [sn]: v })}
          validate={validate}
          getErrorText={getErrorText}
        />
      )}
      <CustomPromptsForm
        prompts={customPrompts}
        value={customPromptsAndSeparatorValue.prompts}
        onChange={(cp, v) =>
          setCustomPromptsAndSeparatorValue(prev => ({
            ...prev,
            prompts: {
              ...prev.prompts,
              [cp]: v,
            },
          }))
        }
        validate={validate}
        getErrorText={getErrorText}
      />
    </div>
  );
};

export default SerialNumberEditForm;
